"use client";

import { useState, useEffect } from "react";
import { X, Globe, Monitor, Smartphone } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { getTranslations } from "@/lib/i18n";

const REMINDER_KEY = "cc-download-reminder-last";
const REMINDER_INTERVAL_MS = 10 * 24 * 60 * 60 * 1000; // 10 days

// Placeholder URLs — replace with real links when available
const DOWNLOAD_LINKS = {
  website: "#", // Mobile PWA / website
  desktop: "#", // Electron / Tauri desktop app
  android: "#", // Google Play Store
  iphone: "#",  // Apple App Store
};

export const SOCIAL_LINKS = {
  facebook: "#",
  instagram: "#",
  linkedin: "#",
  youtube: "#",
  whatsapp: "#",
};

function shouldShowReminder(): boolean {
  try {
    const last = localStorage.getItem(REMINDER_KEY);
    if (!last) return true;
    return Date.now() - Number(last) > REMINDER_INTERVAL_MS;
  } catch {
    return false;
  }
}

function dismissReminder() {
  try {
    localStorage.setItem(REMINDER_KEY, String(Date.now()));
  } catch {
    // Ignore localStorage errors - reminder will show again later
  }
}

// ─── Popup (center screen, every 10 days) ───────────────

export function DownloadReminderPopup() {
  const [show, setShow] = useState(false);
  const { language } = useSettings();
  const t = getTranslations(language);
  const dl = t.downloads as Record<string, string>;

  useEffect(() => {
    // Delay check so it doesn't flash on page load
    const timer = setTimeout(() => {
      if (shouldShowReminder()) setShow(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Manual trigger from sidebar buttons
  useEffect(() => {
    const handler = () => setShow(true);
    window.addEventListener("cc-show-download-reminder", handler);
    return () => window.removeEventListener("cc-show-download-reminder", handler);
  }, []);

  if (!show) return null;

  const handleDismiss = () => {
    dismissReminder();
    setShow(false);
  };

  const platforms = [
    { key: "website", icon: Globe, href: DOWNLOAD_LINKS.website },
    { key: "desktop", icon: Monitor, href: DOWNLOAD_LINKS.desktop },
    { key: "android", icon: Smartphone, href: DOWNLOAD_LINKS.android },
    { key: "iphone", icon: Smartphone, href: DOWNLOAD_LINKS.iphone },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 z-[61] w-[90vw] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-700 p-6 shadow-2xl" style={{ backgroundColor: "var(--nav-bg)" }}>
        {/* Close */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-3 right-3 rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="mb-5 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--cc-accent-600-20)]">
            <Smartphone className="h-6 w-6 text-[var(--cc-accent-400)]" />
          </div>
          <h3 className="text-base font-semibold text-slate-100">{dl.reminderTitle}</h3>
          <p className="mt-1 text-xs text-slate-500">{dl.reminderSubtitle}</p>
        </div>

        {/* Platform buttons */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {platforms.map(({ key, icon: PlatformIcon, href }) => (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-xl border border-slate-700/50 px-3 py-2.5 text-xs font-medium text-slate-300 transition-colors hover:border-[var(--cc-accent-500)]/30 hover:bg-[var(--cc-accent-600-20)] hover:text-[var(--cc-accent-300)]"
            >
              <PlatformIcon className="h-4 w-4 shrink-0" />
              {dl[key]}
            </a>
          ))}
        </div>

        {/* Social links */}
        <div className="border-t border-slate-700/50 pt-3 mb-3">
          <p className="mb-2 text-center text-[10px] font-medium uppercase tracking-wider text-slate-600">
            {dl.gamOnline}
          </p>
          <div className="flex items-center justify-center gap-3">
            {Object.entries(SOCIAL_LINKS).map(([name, href]) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300"
                title={name}
              >
                <SocialIcon name={name} />
              </a>
            ))}
          </div>
        </div>

        {/* Dismiss */}
        <button
          type="button"
          onClick={handleDismiss}
          className="w-full rounded-lg py-2 text-xs text-slate-600 transition-colors hover:text-slate-400"
        >
          {dl.later}
        </button>
      </div>
    </>
  );
}

// ─── Simple SVG social icons ────────────────────────────

export function SocialIcon({ name }: { name: string }) {
  const cls = "h-4 w-4";
  switch (name) {
    case "facebook":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      );
    case "instagram":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case "youtube":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    case "whatsapp":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      );
    default:
      return <Globe className={cls} />;
  }
}
